import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import PostFactoEntryFormDialog from "../PostFactoEntryFormDialog";
import LineFormDialog from "../LineFormDialog";

const CASH_ROLES = ["Director", "Manager", "Accounts", "Back Office"];
const UPI_ROLES = ["Director", "Manager", "Accounts", "Back Office"];
const LINE_ROLES = ["Director", "Manager", "Accounts", "Back Office"];

// "Log Advance" shortcut: opens the normal Cash / UPI / RTGS-NEFT form with Category = Imprest preselected.
export default function ImprestLogAdvanceFlow({ refData, employeeId, onClose, onSaved }) {
  const { user } = useAuth();
  const [step, setStep] = useState("mode");
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState("");
  const [busy, setBusy] = useState(false);
  const preset = { employeeId };
  const modes = [
    { key: "Cash", label: "Cash", roles: CASH_ROLES },
    { key: "UPI", label: "UPI", roles: UPI_ROLES },
    { key: "line", label: "RTGS / NEFT", roles: LINE_ROLES },
  ].filter((m) => m.roles.includes(user?.role));

  useEffect(() => {
    if (step !== "list") return;
    client.get("/payment-desk/payment-lists", { params: { mine: true, page_size: 100 } })
      .then((res) => setLists((res.data.items || res.data).filter((l) => !l.submitted)))
      .catch((err) => toast.error(getErrorMessage(err)));
  }, [step]);

  const chooseList = async (id) => {
    if (id) { setListId(id); setStep("line"); return; }
    setBusy(true);
    try {
      const res = await client.post("/payment-desk/payment-lists");
      setListId(res.data.list_id);
      setStep("line");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (step === "Cash" || step === "UPI") {
    return <PostFactoEntryFormDialog mode={step} refData={refData} imprestPreset={preset} onClose={onClose} onSaved={onSaved} />;
  }
  if (step === "line") {
    return <LineFormDialog listId={listId} refData={refData} imprestPreset={preset} onClose={onClose} onSaved={onSaved} />;
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="imprest-advance-mode-dialog">
        <DialogHeader>
          <DialogTitle>{step === "mode" ? "Log Imprest Advance" : "Choose a Payment List"}</DialogTitle>
          <DialogDescription>
            {step === "mode" ? "How was or will the advance be paid? The usual form opens with Category = Imprest." : "RTGS/NEFT advances are lines on a Payment List. They count in the balance once marked Paid."}
          </DialogDescription>
        </DialogHeader>
        {step === "mode" ? (
          <div className="grid gap-2">
            {modes.map((m) => (
              <Button key={m.key} variant="outline" className="justify-start" data-testid={`imprest-advance-mode-${m.key.toLowerCase()}`} onClick={() => setStep(m.key === "line" ? "list" : m.key)}>{m.label}</Button>
            ))}
          </div>
        ) : (
          <div className="grid gap-2" data-testid="imprest-advance-list-picker">
            {lists.map((l) => (
              <Button key={l.list_id} variant="outline" className="justify-start" disabled={busy} data-testid={`imprest-advance-list-${l.list_id}`} onClick={() => chooseList(l.list_id)}>{l.list_id} (open)</Button>
            ))}
            <Button className="justify-start bg-amber-600 hover:bg-amber-700" disabled={busy} data-testid="imprest-advance-new-list" onClick={() => chooseList("")}>+ New Payment List</Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="imprest-advance-mode-cancel">Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
